import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    AlertCircle,
    ArrowUpRight,
    Bell,
    CheckCircle2,
    Clock4,
    DollarSign,
    Filter,
    FilePlus2,
    Search,
    UserPlus,
} from 'lucide-react';
import { useAdminContext } from '../../context/AdminContext.js';
import useNotifications from '../../hooks/useNotifications.js';
import Pagination from '../../components/common/Pagination.js';
import '../../styles/AdminNotifications.css';

const TYPE_LABELS = {
    new_agreement: 'Nuevo acuerdo',
    new_withdrawal_request: 'Solicitud de retiro',
    new_small_owner_request: 'Solicitud de pequeño dueño',
};

const TYPE_ICONS = {
    new_agreement: FilePlus2,
    new_withdrawal_request: DollarSign,
    new_small_owner_request: UserPlus,
};

const getNotificationIcon = (type, isUnread) => {
    const Icon = TYPE_ICONS[type] || Bell;
    const className = `admin-notifications__icon${isUnread ? ' admin-notifications__icon--unread' : ''}`;
    return <Icon size={18} className={className} />;
};

const formatRelativeTime = (timestamp) => {
    if (!timestamp) {
        return 'Sin fecha';
    }
    const date = typeof timestamp.toDate === 'function' ? timestamp.toDate() : timestamp;
    return formatDistanceToNow(date, { addSuffix: true, locale: es });
};

const deriveTitle = (notification) => {
    if (!notification) {
        return 'Notificación';
    }
    return notification.title || TYPE_LABELS[notification.type] || 'Notificación';
};

const AdminNotifications = () => {
    const { currentUser: user, userProfile } = useAdminContext();
    const navigate = useNavigate();
    const canViewAdminNotifications = Boolean(
        userProfile?.isAdmin || userProfile?.permissions?.['panel:notificaciones']
    );

    const {
        notifications,
        unreadCount,
        loading,
        markAllAsRead,
        markAsRead,
    } = useNotifications(user?.uid, canViewAdminNotifications, {
        soundsEnabled: userProfile?.notificationSoundsEnabled !== false,
        soundFile: 'notification.mp3',
    });

    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [selectedNotification, setSelectedNotification] = useState(null);

    useEffect(() => {
        if (!selectedNotification && notifications.length > 0) {
            setSelectedNotification(notifications[0]);
        }
    }, [notifications, selectedNotification]);

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, typeFilter, searchTerm]);

    const filteredNotifications = useMemo(() => {
        const search = searchTerm.trim().toLowerCase();
        return notifications.filter((notification) => {
            const matchesStatus =
                statusFilter === 'all'
                    ? true
                    : statusFilter === 'unread'
                        ? !notification.read
                        : notification.read;
            const matchesType = typeFilter === 'all' ? true : notification.type === typeFilter;
            const matchesSearch =
                search.length === 0 ||
                [notification.title, notification.message, notification.link]
                    .filter(Boolean)
                    .some((value) => value.toLowerCase().includes(search));
            return matchesStatus && matchesType && matchesSearch;
        });
    }, [notifications, statusFilter, typeFilter, searchTerm]);

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(filteredNotifications.length / itemsPerPage) || 1);
        if (currentPage > maxPage) {
            setCurrentPage(maxPage);
        }
    }, [filteredNotifications.length, itemsPerPage, currentPage]);

    useEffect(() => {
        if (selectedNotification && !filteredNotifications.some((item) => item.id === selectedNotification.id)) {
            setSelectedNotification(filteredNotifications[0] || null);
        }
    }, [filteredNotifications, selectedNotification]);

    const paginatedNotifications = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredNotifications.slice(start, start + itemsPerPage);
    }, [filteredNotifications, currentPage, itemsPerPage]);

    const typeOptions = useMemo(() => {
        const values = new Set();
        notifications.forEach((notification) => {
            if (notification.type) {
                values.add(notification.type);
            }
        });
        return Array.from(values);
    }, [notifications]);

    const stats = useMemo(() => ({
        total: notifications.length,
        unread: unreadCount,
        read: notifications.length - unreadCount,
    }), [notifications.length, unreadCount]);

    const totalPages = Math.max(1, Math.ceil(filteredNotifications.length / itemsPerPage) || 1);

    const handleSelectNotification = (notification) => {
        setSelectedNotification(notification);
        if (!notification.read) {
            markAsRead(notification.id);
        }
    };

    const handleNavigateToLink = () => {
        if (selectedNotification?.link) {
            navigate(`/${selectedNotification.link}`);
        }
    };

    const handleMarkAllAsRead = () => {
        if (unreadCount > 0) {
            markAllAsRead();
        }
    };

    const renderStatusBadge = (isRead) => (
        <span className={`admin-notifications__status-badge${isRead ? ' is-read' : ' is-unread'}`}>
            {isRead ? 'Leída' : 'No leída'}
        </span>
    );

    if (!canViewAdminNotifications) {
        return (
            <div className="admin-notifications admin-notifications--no-access">
                <div className="admin-notifications__header">
                    <div className="admin-notifications__title">
                        <h2><Bell size={20} /> Centro de notificaciones</h2>
                        <p>No tienes permisos para ver las notificaciones globales.</p>
                    </div>
                </div>
                <div className="admin-notifications__empty">
                    <AlertCircle size={32} />
                    <p>Pide a un administrador que habilite el permiso <code>panel:notificaciones</code> si necesitas acceso.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-notifications">
            <header className="admin-notifications__header">
                <div className="admin-notifications__title">
                    <h2><Bell size={20} /> Centro de notificaciones</h2>
                    <p>Monitorea y gestiona las alertas generadas dentro de la plataforma.</p>
                </div>
                <button
                    type="button"
                    onClick={handleMarkAllAsRead}
                    className="admin-notifications__action"
                    disabled={unreadCount === 0}
                >
                    <CheckCircle2 size={16} /> Marcar todas como leídas ({unreadCount})
                </button>
            </header>

            <section className="admin-notifications__stats">
                <div className="admin-notifications__stats-item">
                    <Bell size={16} />
                    <span>Total</span>
                    <strong>{stats.total}</strong>
                </div>
                <div className="admin-notifications__stats-item">
                    <Clock4 size={16} />
                    <span>No leídas</span>
                    <strong>{stats.unread}</strong>
                </div>
                <div className="admin-notifications__stats-item">
                    <CheckCircle2 size={16} />
                    <span>Leídas</span>
                    <strong>{stats.read}</strong>
                </div>
            </section>

            <section className="admin-notifications__controls">
                <div className="admin-notifications__search">
                    <Search size={16} />
                    <input
                        type="search"
                        placeholder="Buscar por título, mensaje o enlace"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                    />
                </div>
                <div className="admin-notifications__filters">
                    <Filter size={16} />
                    <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                        <option value="all">Todos los estados</option>
                        <option value="unread">No leídas</option>
                        <option value="read">Leídas</option>
                    </select>
                    <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
                        <option value="all">Todos los tipos</option>
                        {typeOptions.map((type) => (
                            <option key={type} value={type}>
                                {TYPE_LABELS[type] || type}
                            </option>
                        ))}
                    </select>
                </div>
            </section>

            <div className="admin-notifications__layout">
                <section className="admin-notifications__list">
                    {loading ? (
                        <div className="admin-notifications__loading">
                            <Clock4 size={20} className="spin" />
                            <span>Cargando notificaciones...</span>
                        </div>
                    ) : paginatedNotifications.length === 0 ? (
                        <div className="admin-notifications__empty">
                            <AlertCircle size={32} />
                            <p>No encontramos notificaciones con los filtros aplicados.</p>
                        </div>
                    ) : (
                        paginatedNotifications.map((notification) => {
                            const title = deriveTitle(notification);
                            return (
                                <button
                                    key={notification.id}
                                    type="button"
                                    className={`admin-notifications__item${!notification.read ? ' is-unread' : ''}${selectedNotification?.id === notification.id ? ' is-active' : ''}`}
                                    onClick={() => handleSelectNotification(notification)}
                                >
                                    <div className="admin-notifications__item-icon">
                                        {getNotificationIcon(notification.type, !notification.read)}
                                    </div>
                                    <div className="admin-notifications__item-content">
                                        <div className="admin-notifications__item-header">
                                            <h3>{title}</h3>
                                            {renderStatusBadge(notification.read)}
                                        </div>
                                        <p>{notification.message || 'Sin detalles adicionales.'}</p>
                                        <div className="admin-notifications__item-meta">
                                            <span>{formatRelativeTime(notification.createdAt)}</span>
                                            {notification.type && (
                                                <span className="admin-notifications__type-chip">{TYPE_LABELS[notification.type] || notification.type}</span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })
                    )}

                    {!loading && filteredNotifications.length > itemsPerPage && (
                        <div className="admin-notifications__pagination">
                            <Pagination
                                totalItems={filteredNotifications.length}
                                itemsPerPage={itemsPerPage}
                                onItemsPerPageChange={setItemsPerPage}
                                currentPage={currentPage}
                                onNext={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                                onPrevious={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                                canNext={currentPage < totalPages}
                                canPrevious={currentPage > 1}
                            />
                        </div>
                    )}
                </section>

                <aside className="admin-notifications__detail">
                    {selectedNotification ? (
                        <>
                            <header>
                                <h3>{deriveTitle(selectedNotification)}</h3>
                                {renderStatusBadge(selectedNotification.read)}
                            </header>
                            <p className="admin-notifications__detail-message">
                                {selectedNotification.message || 'Sin descripción disponible.'}
                            </p>
                            <div className="admin-notifications__detail-meta">
                                <div>
                                    <span className="label">Estado</span>
                                    {renderStatusBadge(selectedNotification.read)}
                                </div>
                                <div>
                                    <span className="label">Tipo</span>
                                    <span className="admin-notifications__type-chip">
                                        {TYPE_LABELS[selectedNotification.type] || selectedNotification.type || 'General'}
                                    </span>
                                </div>
                                <div>
                                    <span className="label">Generada</span>
                                    <span>{formatRelativeTime(selectedNotification.createdAt)}</span>
                                </div>
                            </div>
                            {selectedNotification.link && (
                                <button
                                    type="button"
                                    className="admin-notifications__cta"
                                    onClick={handleNavigateToLink}
                                >
                                    Ir a la sección <ArrowUpRight size={16} />
                                </button>
                            )}
                            {selectedNotification.metadata && (
                                <div className="admin-notifications__detail-metadata">
                                    <span className="label">Metadata</span>
                                    <pre>{JSON.stringify(selectedNotification.metadata, null, 2)}</pre>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="admin-notifications__detail-empty">
                            <Clock4 size={24} />
                            <p>Selecciona una notificación para ver su detalle.</p>
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
};

export default AdminNotifications;